import { REMEMBER_LOGIN_KEY } from '../constants/storage';
import { localStorageAdapter } from '../services/storage';
import { AppConfig } from '../types/app';

const HOUR_MS = 60 * 60 * 1000;
const DEFAULT_AUTO_REDIRECT_MS = 24 * HOUR_MS;
// After the auto-redirect window, the email chooser is offered for this long.
export const REMEMBER_LOGIN_CHOOSER_WINDOW_MS = 24 * HOUR_MS;

// What the page remembers about "Remember how I sign in". It holds no email: the
// edge gets that from the session's refresh token.
export interface RememberLoginEntry {
  lastSelectedIDP: string;
  loginMethodResponse: string[];
  /** End of the auto-redirect window (epoch ms); the chooser window follows. */
  ttl: number;
  /** Set for social sign-ins (e.g. 'Google'); those always go through AWS Builder ID. */
  socialProvider?: string;
}

function autoRedirectWindowMs(
  config: Pick<AppConfig, 'rememberLoginAutoRedirectMinutes'>
) {
  const minutes = Number(config.rememberLoginAutoRedirectMinutes);
  return Number.isFinite(minutes) && minutes > 0
    ? minutes * 60 * 1000
    : DEFAULT_AUTO_REDIRECT_MS;
}

export function clearRememberLogin(): void {
  localStorageAdapter.removeItem(REMEMBER_LOGIN_KEY);
}

export function saveRememberLogin(
  config: Pick<AppConfig, 'rememberLoginAutoRedirectMinutes'>,
  selection: {
    lastSelectedIDP?: string;
    loginMethodResponse?: string[];
    socialProvider?: string;
  }
): void {
  if (!selection.lastSelectedIDP) {
    clearRememberLogin();
    return;
  }
  const entry: RememberLoginEntry = {
    lastSelectedIDP: selection.lastSelectedIDP,
    loginMethodResponse: selection.loginMethodResponse ?? [],
    ttl: Date.now() + autoRedirectWindowMs(config),
    ...(selection.socialProvider && {
      socialProvider: selection.socialProvider,
    }),
  };
  localStorageAdapter.setItem(REMEMBER_LOGIN_KEY, JSON.stringify(entry));
}

// Returns the stored entry while it is still inside the auto-redirect or chooser
// window; drops it (and returns null) when it is missing, malformed or expired.
export function readRememberLogin(now = Date.now()): RememberLoginEntry | null {
  const raw = localStorageAdapter.getItem(REMEMBER_LOGIN_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.lastSelectedIDP === 'string' &&
      parsed.lastSelectedIDP &&
      Array.isArray(parsed.loginMethodResponse) &&
      parsed.loginMethodResponse.every((m: unknown) => typeof m === 'string') &&
      typeof parsed.ttl === 'number' &&
      now <= parsed.ttl + REMEMBER_LOGIN_CHOOSER_WINDOW_MS &&
      (parsed.socialProvider === undefined ||
        (typeof parsed.socialProvider === 'string' && parsed.socialProvider))
    ) {
      return {
        lastSelectedIDP: parsed.lastSelectedIDP,
        loginMethodResponse: parsed.loginMethodResponse,
        ttl: parsed.ttl,
        ...(parsed.socialProvider && { socialProvider: parsed.socialProvider }),
      };
    }
  } catch {
    // Fall through: unreadable entries are dropped.
  }
  clearRememberLogin();
  return null;
}
